import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, Loader2, RotateCcw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => (parseFloat(v) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const dateFr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '-');

// Paiements annulés de la souscription, restaurables par le super admin
export const PaiementsAnnules = ({ doc, getAuthHeader, onDone }) => {
  const { user } = useAuth();
  const superAdmin = (user?.role || '').toLowerCase() === 'super_admin';
  const [liste, setListe] = useState([]);
  const [busy, setBusy] = useState(null);
  const nbAnnules = (doc.paiements_annules || []).length;

  const charger = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/souscriptions/admin/${doc.id}/paiements-annules`, { headers: getAuthHeader() });
      setListe(res.data.paiements || []);
    } catch { setListe([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id]);

  useEffect(() => { if (superAdmin && nbAnnules > 0) charger(); else setListe([]); }, [superAdmin, charger, nbAnnules, doc.total_paye]);

  const restaurer = async (p) => {
    if (!window.confirm(`Restaurer le paiement de ${eur(p.montant)} du ${dateFr(p.date_paiement)} ?\n\nIl revient dans l'historique et le reçu acquitté sera régénéré.`)) return;
    const cle = `${p.id}-${p.annule_at}`;
    setBusy(cle);
    try {
      const res = await axios.post(`${API}/souscriptions/admin/${doc.id}/paiements-annules/restaurer`, {},
        { headers: getAuthHeader(), params: { paiement_id: p.id, annule_at: p.annule_at } });
      toast.success(res.data?.message || 'Paiement restauré');
      await charger();
      await onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Restauration impossible'); }
    finally { setBusy(null); }
  };

  if (!superAdmin || !liste.length) return null;
  return (
    <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50/60 p-2" data-testid={`paiements-annules-${doc.id}`}>
      <p className="text-xs font-semibold text-rose-800 mb-1 flex items-center gap-1"><History className="h-3.5 w-3.5" /> Paiements annulés</p>
      {liste.map((p) => {
        const cle = `${p.id}-${p.annule_at}`;
        return (
          <div key={cle} className="flex items-center gap-2 text-xs py-0.5" data-testid={`paiement-annule-${p.id}`}>
            <span className="w-20">{dateFr(p.date_paiement)}</span>
            <span className="font-medium w-24">{eur(p.montant)}</span>
            <span className="flex-1 text-slate-500 truncate">{p.motif || p.mode_paiement || ''} — annulé le {dateFr(p.annule_at)}</span>
            <button type="button" onClick={() => restaurer(p)} disabled={busy === cle}
              className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 disabled:opacity-50" data-testid={`paiement-annule-restaurer-${p.id}`}>
              {busy === cle ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Restaurer
            </button>
          </div>
        );
      })}
    </div>
  );
};
